import {Types} from 'mongoose';
import {Employee} from '@/interfaces/employee.interface';
import {LabeledAvailabilityInterface} from '@/interfaces/availability.interface';
import {AvailabilityLabels} from '@/enums/AvailabilityLabels';
import {ApplicationState} from '@/enums/recruiter/ApplicationState.enum';
import {EmployeeHandlerAPI} from '@/classes/api/employee-handler.api.class';
import {Place} from '@/interfaces/place.interface';

export type PickerMode = 'assign' | 'plan';
export type PickerWho = 'teachers' | 'applicants' | 'all';

export type Reason = { text: string; icon: string; tone: string; title?: string };
export type DayState = 'match' | 'available' | 'wanted' | 'none';

export type Candidate = {
    id: string;
    employee: Employee;
    name: string;
    initials: string;
    city: string;
    distanceKm: number | null;
    matchDays: number[];
    assignments: number;
    score: number;
    reasons: Reason[];
    days: DayState[];
    disabledReason: string | null;
};

export const WORK_DAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr'];
export const RADIUS_STEPS = [5, 10, 20, 40];
/** Geo requests are loaded once with the biggest radius, the radius pills only filter on the client */
export const MAX_RADIUS_KM = 40;

export const EMPLOYEE_FIELDS = ['firstName', 'lastName', 'address', 'subjects', 'availabilities', 'hasCar', 'hasDriverLicense', 'maxDistance',
    'maxTravelTime', 'assignedInstitutions', 'plannedInstitutions', 'isApplicant', 'isTeacher', 'vanillaApplication'];

const EARTH_RADIUS_KM = 6371;

/** Air-line distance in km between two places (GeoJSON coordinates are [lng, lat]); null if one address has no location */
export function airDistanceKm(a?: Place | null, b?: Place | null): number | null {
    const ca = a?.location?.coordinates;
    const cb = b?.location?.coordinates;
    if (!ca || !cb || ca.length !== 2 || cb.length !== 2 || (!ca[0] && !ca[1]) || (!cb[0] && !cb[1])) {
        return null;
    }
    const toRad = (value: number) => value * Math.PI / 180;
    const dLat = toRad(cb[1] - ca[1]);
    const dLng = toRad(cb[0] - ca[0]);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(ca[1])) * Math.cos(toRad(cb[1])) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}

export function formatKm(km: number): string {
    return `${km.toLocaleString('de-DE', {maximumFractionDigits: km < 10 ? 1 : 0})} km`;
}

/** Only slots meant for courses count (slots without labels are treated as course slots) */
export function courseSlots(items?: LabeledAvailabilityInterface[]): LabeledAvailabilityInterface[] {
    return (items || []).filter((el) => el?.timeRange && (!el.labels?.length || el.labels.includes(AvailabilityLabels.FOR_COURSES)));
}

/** Weekdays (0 = Monday) on which teacher and school have overlapping time ranges */
export function matchingDays(teacher?: LabeledAvailabilityInterface[], school?: LabeledAvailabilityInterface[]): number[] {
    const own = courseSlots(teacher);
    const wanted = courseSlots(school);
    const days = new Set<number>();
    for (const slot of wanted) {
        const hit = own.some((el) => el.dayOfWeek === slot.dayOfWeek
            && el.timeRange.startTime < slot.timeRange.endTime && el.timeRange.endTime > slot.timeRange.startTime);
        if (hit) {
            days.add(slot.dayOfWeek);
        }
    }
    return Array.from(days).sort((a, b) => a - b);
}

export function dayStates(teacher: LabeledAvailabilityInterface[] | undefined, school: LabeledAvailabilityInterface[] | undefined, match: number[]): DayState[] {
    const own = new Set(courseSlots(teacher).map((el) => el.dayOfWeek));
    const wanted = new Set(courseSlots(school).map((el) => el.dayOfWeek));
    return WORK_DAYS.map((_, day) => {
        if (match.includes(day)) {
            return 'match';
        }
        if (own.has(day)) {
            return 'available';
        }
        return wanted.has(day) ? 'wanted' : 'none';
    });
}

export function containsId(list: unknown[] | undefined, id: string): boolean {
    return (list || []).some((el) => String((el as { _id?: unknown })?._id ?? el) === id);
}

export function applicationState(employee: Employee): ApplicationState | undefined {
    return employee.vanillaApplication?.applicationState as ApplicationState | undefined;
}

function applicantReason(employee: Employee): Reason | null {
    if (!employee.isApplicant) {
        return null;
    }
    const state = applicationState(employee);
    const text = state && ApplicationState.Record[state] ? `Bewerbung: ${ApplicationState.Record[state].text}` : 'Bewerber:in';
    const tone = state === ApplicationState.ACCEPTED ? 'tone-good' : state === ApplicationState.IN_PROGRESS ? 'tone-info' : 'tone-muted';
    return {text, icon: 'mdi-account-clock-outline', tone};
}

type Scored = { score: number; reasons: Reason[] };

function scoreAvailability(match: number[], employee: Employee, schoolHasSlots: boolean): Scored {
    if (match.length) {
        return {
            score: 30 + 5 * (match.length - 1),
            reasons: [{text: `passt ${match.map((day) => WORK_DAYS[day]).join(', ')}`, icon: 'mdi-calendar-check-outline', tone: 'tone-good',
                title: 'Verfügbarkeit überschneidet sich mit den Kurszeiten der Schule'}],
        };
    }
    if (!courseSlots(employee.availabilities).length) {
        return {score: 0, reasons: [{text: 'keine Verfügbarkeit hinterlegt', icon: 'mdi-calendar-question', tone: 'tone-muted'}]};
    }
    return schoolHasSlots ? {score: -5, reasons: [{text: 'Zeiten passen nicht', icon: 'mdi-calendar-remove-outline', tone: 'tone-muted'}]}
        : {score: 0, reasons: []};
}

function scoreDistance(employee: Employee, km: number | null): Scored {
    if (km === null) {
        return {score: -10, reasons: [{text: 'Wohnort unbekannt', icon: 'mdi-home-alert-outline', tone: 'tone-warn'}]};
    }
    let score = Math.max(0, (MAX_RADIUS_KM - km) / 2);
    const reasons: Reason[] = [];
    if (employee.maxDistance) {
        if (km <= employee.maxDistance) {
            score += 15;
            reasons.push({text: 'im Wunschradius', icon: 'mdi-map-marker-check-outline', tone: 'tone-good', title: `möchte bis ${employee.maxDistance} km fahren`});
        } else {
            score -= 10;
            reasons.push({text: `wünscht max. ${employee.maxDistance} km`, icon: 'mdi-map-marker-alert-outline', tone: 'tone-warn'});
        }
    }
    if (km > 10) {
        if (employee.hasCar && employee.hasDriverLicense) {
            score += 5;
            reasons.push({text: 'mit Auto', icon: 'mdi-car-outline', tone: 'tone-info'});
        } else {
            score -= 8;
            reasons.push({text: 'ohne Auto', icon: 'mdi-car-off', tone: 'tone-warn', title: 'Mehr als 10 km Luftlinie ohne Auto'});
        }
    }
    return {score, reasons};
}

function scoreLoad(assignments: number): Scored {
    if (!assignments) {
        return {score: 10, reasons: [{text: 'noch keine Schule', icon: 'mdi-school-outline', tone: 'tone-good', title: 'Noch keiner Schule zugeteilt'}]};
    }
    if (assignments <= 2) {
        return {score: 5, reasons: []};
    }
    return {score: assignments >= 4 ? -5 : 0, reasons: [{text: `schon ${assignments} Schulen`, icon: 'mdi-school-outline', tone: 'tone-muted'}]};
}

/** Builds the ranked row of one person (score + "Warum vorgeschlagen" tags) */
export function toCandidate(employee: Employee, school: { address?: Place; availabilities?: LabeledAvailabilityInterface[] } | null, institutionId: string,
                            mode: PickerMode): Candidate {
    const id = String(employee._id);
    const distanceKm = airDistanceKm(employee.address, school?.address);
    const match = matchingDays(employee.availabilities, school?.availabilities);
    const assignments = (employee.assignedInstitutions || []).length;
    const parts = [
        scoreAvailability(match, employee, courseSlots(school?.availabilities).length > 0),
        scoreDistance(employee, distanceKm),
        scoreLoad(assignments),
    ];
    const reasons: Reason[] = [];
    let score = 0;
    const plannedHere = containsId(employee.plannedInstitutions, institutionId);
    const assignedHere = containsId(employee.assignedInstitutions, institutionId);
    if (mode === 'assign' && plannedHere) {
        score += 20;
        reasons.push({text: 'hier vorgemerkt', icon: 'mdi-bookmark-check-outline', tone: 'tone-purple'});
    }
    if (mode === 'plan' && assignedHere) {
        reasons.push({text: 'hier schon zugeteilt', icon: 'mdi-account-check-outline', tone: 'tone-purple'});
    }
    parts.forEach((part) => {
        score += part.score;
        reasons.push(...part.reasons);
    });
    const applicant = applicantReason(employee);
    if (applicant) {
        reasons.push(applicant);
    }
    const first = employee.firstName || '';
    const last = employee.lastName || '';
    return {
        id,
        employee,
        name: `${first} ${last}`.trim() || 'Unbekannt',
        initials: `${first[0] || ''}${last[0] || ''}`.toUpperCase() || '?',
        city: employee.address?.city || '',
        distanceKm,
        matchDays: match,
        assignments,
        score,
        reasons,
        days: dayStates(employee.availabilities, school?.availabilities, match),
        disabledReason: null,
    };
}

export function byScore(a: Candidate, b: Candidate): number {
    return b.score - a.score || (a.distanceKm ?? Number.MAX_SAFE_INTEGER) - (b.distanceKm ?? Number.MAX_SAFE_INTEGER);
}

/** Loads active teachers and active applicants with the same base filter (geo or $search) and merges them */
export async function findPeople(base: Record<string, unknown>, extra: Record<string, unknown> = {}): Promise<Employee[]> {
    const find = (filter: Record<string, unknown>) => EmployeeHandlerAPI.find({
        filter: {...base, isActive: true, ...filter},
        fields: EMPLOYEE_FIELDS,
        populate: {subjects: {fields: ['name', 'color']}},
        ...extra,
    } as any).then((resp) => resp.employees);
    const [teachers, applicants] = await Promise.all([find({isTeacher: true, isApplicant: false}), find({isApplicant: true})]);
    const seen = new Set<string>();
    return [...teachers, ...applicants].filter((employee) => {
        const id = String(employee._id);
        if (seen.has(id) || applicationState(employee) === ApplicationState.REJECTED) {
            return false;
        }
        seen.add(id);
        return true;
    });
}

/** Ids arrive as strings from the API anyway, the typed ObjectId is only needed for the API signatures */
export function asObjectId(id: string): Types.ObjectId {
    return id as unknown as Types.ObjectId;
}

export type AssignmentChange = { added: Types.ObjectId[]; removed: Types.ObjectId[] };

/** Saves all changes; returns the number of failed requests */
export async function saveAssignments(mode: PickerMode, institutionId: Types.ObjectId, change: AssignmentChange): Promise<number> {
    const data = {type: mode, institutionId};
    const results = await Promise.allSettled([
        ...change.added.map((id) => EmployeeHandlerAPI.addAssignment(id, data)),
        ...change.removed.map((id) => EmployeeHandlerAPI.removeAssignment(id, data)),
    ]);
    return results.filter((el) => el.status === 'rejected').length;
}
